import * as React from "react";
import { ArrowRight } from "lucide-react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export type CtaButtonProps = Omit<ButtonProps, "variant"> & {
  tone?: "light" | "dark";
};

/**
 * The loudest button on the page. The lift is a static box-shadow; the pulse
 * is a compositor-only opacity animation on ::after (see `cta-glow` in
 * styles.css), so the button never repaints a shadow per frame. The sheen gets
 * its own clipping wrapper rather than `overflow-hidden` on the button, which
 * would clip the glow layer.
 */
export const CtaButton = React.forwardRef<HTMLButtonElement, CtaButtonProps>(
  ({ asChild = false, children, className, tone = "light", ...props }, ref) => {
    const toneClass =
      tone === "dark"
        ? "cta-glow-dark bg-brand-400 text-on-brand-400 hover:bg-brand-300 focus-visible:ring-brand-400"
        : "cta-glow";
    const decoration = (
      <>
        <span
          className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]"
          aria-hidden="true"
        >
          <span className="cta-sheen" />
        </span>
        <ArrowRight className="relative z-10" aria-hidden="true" />
      </>
    );

    if (asChild && React.isValidElement<{ children?: React.ReactNode }>(children)) {
      return (
        <Button
          ref={ref}
          asChild
          variant="primary"
          className={cn("relative", toneClass, className)}
          {...props}
        >
          {React.cloneElement(
            children,
            undefined,
            <>
              <span className="relative z-10">{children.props.children}</span>
              {decoration}
            </>,
          )}
        </Button>
      );
    }

    return (
      <Button
        ref={ref}
        variant="primary"
        className={cn("relative", toneClass, className)}
        {...props}
      >
        <span className="relative z-10">{children}</span>
        {decoration}
      </Button>
    );
  },
);

CtaButton.displayName = "CtaButton";

export default CtaButton;
